import { Suspense, lazy } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import AppShell from "./layout/AppShell";
import ErrorBoundary from "./components/ErrorBoundary";
import { useAuth } from "./context/AuthContext";
import { entradaDeRuta } from "./layout/navigation";
import { AMBITO_NEGOCIO } from "./lib/constants";
import { EmptyState, Spinner } from "./components/ui";
import LoginPage from "./modules/auth/LoginPage";

/* =========================================================================
   RUTAS DEL PANEL DE ALIADOS

   UN ÁRBOL POR ÁMBITO, no uno solo con las nueve pantallas. Los dos oficios
   reclaman la raíz —la portería del celador y los pedidos del tendero— y con
   un árbol único habría que decidir cuál gana y echar al otro de su propia
   pantalla de inicio. Separados, cada quien entra donde le corresponde y una
   dirección del otro oficio sencillamente no existe: no hay 403 que explicar
   porque no hay nada que proteger.

   La estructura interna es la del panel interno a propósito: `RequireAuth`
   para la sesión, `RequireModule` para el permiso y `ErrorBoundary` por ruta.
   Reescribirla más simple habría significado dos formas distintas de resolver
   el mismo problema en dos proyectos que se mantienen juntos.
   ======================================================================== */

const ResumenConjuntoPage = lazy(
  () => import("./modules/conjunto/ResumenPage"),
);
const ReportesConjuntoPage = lazy(
  () => import("./modules/conjunto/ReportesPage"),
);
const MiConjuntoPage = lazy(() => import("./modules/conjunto/MiConjuntoPage"));
const PorteriaPage = lazy(() => import("./modules/porteria/PorteriaPage"));
const EntradasPage = lazy(() => import("./modules/porteria/EntradasPage"));
const ResidentesPage = lazy(() => import("./modules/celadores/ResidentesPage"));
const CeladoresPage = lazy(() => import("./modules/celadores/CeladoresPage"));

const PedidosPage = lazy(() => import("./modules/negocio/PedidosPage"));
const ProductosPage = lazy(() => import("./modules/negocio/ProductosPage"));
const DomiciliariosPage = lazy(
  () => import("./modules/negocio/DomiciliariosPage"),
);
const ResenasPage = lazy(() => import("./modules/negocio/ResenasPage"));
const IngresosPage = lazy(() => import("./modules/negocio/IngresosPage"));
const ChatPage = lazy(() => import("./modules/negocio/ChatPage"));
const PromocionesPage = lazy(() => import("./modules/negocio/PromocionesPage"));
const TorrePage = lazy(() => import("./modules/negocio/TorrePage"));
const ClientesPage = lazy(() => import("./modules/negocio/ClientesPage"));
const CuentaPage = lazy(() => import("./modules/negocio/CuentaPage"));
const MiNegocioPage = lazy(() => import("./modules/negocio/MiNegocioPage"));

function Cargando({ texto = "Cargando…" }) {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: "50vh" }}>
      <span className="row gap-3 muted">
        <Spinner size={18} /> {texto}
      </span>
    </div>
  );
}

function RequireAuth({ children }) {
  const { isAuthenticated, booting } = useAuth();
  const location = useLocation();

  // Mientras se revalida el token guardado no se decide nada: redirigir acá
  // sacaría al usuario al login en cada recarga.
  if (booting) return <Cargando texto="Verificando sesión…" />;

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return children;
}

/**
 * Puerta por sección dentro del panel.
 *
 * Ocultar la entrada del menú no basta: la URL se puede escribir a mano y
 * quedaría una pantalla intentando cargar datos que el servidor va a rechazar,
 * llenándola de errores en vez de decir qué pasa. Acá se dice.
 *
 * Es comodidad de la interfaz, no seguridad: los middleware `conjunto` y
 * `negocio` del backend rechazan la petición igual aunque esto se saltara.
 */
function RequireModule({ children }) {
  const { ambito, permisos, puedeVer, puedeGestionar } = useAuth();
  const location = useLocation();

  // Permisos todavía en camino: no se decide nada, o se expulsaría a alguien
  // de una sección a la que sí tiene derecho.
  if (permisos === null) return <Cargando texto="Comprobando permisos…" />;

  const entrada = entradaDeRuta(location.pathname, ambito);
  const modulo = entrada?.modulo ?? null;

  if (modulo && !puedeVer(modulo)) {
    return (
      <div className="card">
        <EmptyState
          icon="shield"
          title="Esta sección no es para tu cuenta"
          text="Tu acceso no llega a esta parte del panel. Si crees que debería, pídeselo al equipo."
        />
      </div>
    );
  }

  if (modulo && entrada?.exigeGestion && !puedeGestionar(modulo)) {
    return (
      <div className="card">
        <EmptyState
          icon="shield"
          title="Solo consulta"
          text="Esta sección no tiene una vista de solo lectura. Pídele al equipo que te suba el nivel de acceso si necesitas entrar."
        />
      </div>
    );
  }

  return children;
}

/**
 * Envoltura común de todas las secciones: permiso, carga diferida y límite de
 * error, en ese orden.
 *
 * El límite se reinicia con la ruta —de ahí `claveReinicio`—: sin eso, una
 * pantalla que falló se quedaría puesta encima de la siguiente, que quizá
 * funciona perfectamente.
 */
function ZonaProtegida() {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary claveReinicio={pathname}>
      <RequireModule>
        <Suspense fallback={<Cargando />}>
          <Outlet />
        </Suspense>
      </RequireModule>
    </ErrorBoundary>
  );
}

/*
 * Los dos juegos de pantallas.
 *
 * Son funciones que devuelven `<Route>`, no componentes: React Router exige
 * que los hijos de `<Routes>` sean elementos Route de verdad y no cualquier
 * cosa que acabe pintándolos, así que se invocan —`rutasDeNegocio()`— en vez
 * de montarse como `<RutasDeNegocio />`. Escrito de la segunda forma, el
 * router no encuentra ninguna ruta y el panel entero cae al 404.
 */
const rutasDeNegocio = () => (
  <Route element={<ZonaProtegida />}>
    <Route index element={<PedidosPage />} />
    <Route path="productos" element={<ProductosPage />} />
    <Route path="domiciliarios" element={<DomiciliariosPage />} />
    <Route path="torre" element={<TorrePage />} />
    <Route path="clientes" element={<ClientesPage />} />
    <Route path="chat" element={<ChatPage />} />
    <Route path="promociones" element={<PromocionesPage />} />
    <Route path="ingresos" element={<IngresosPage />} />
    <Route path="resenas" element={<ResenasPage />} />
    <Route path="mi-negocio" element={<MiNegocioPage />} />
    <Route path="mi-cuenta" element={<CuentaPage />} />
  </Route>
);

/**
 * La raíz del conjunto depende del OFICIO, no del ámbito.
 *
 * El celador trabaja en la puerta: entra al panel con una moto esperando y lo
 * que necesita es el campo del código. El administrador no está en la puerta;
 * aterrizar ahí no le dice nada de su edificio.
 *
 * Redirigir es mejor que montar pantallas distintas en la misma dirección:
 * cada una conserva su URL propia, así que un enlace a la portería sigue
 * llevando a la portería sea quien sea quien lo abra.
 */
function InicioDelConjunto() {
  const { nivel } = useAuth();

  // Mientras no se sabe el rol no se redirige: mandar al resumen y corregir
  // después haría parpadear una pantalla que no era.
  if (!nivel) return <Cargando texto="Abriendo…" />;

  return <Navigate to={nivel === "dueno" ? "/resumen" : "/porteria"} replace />;
}

const rutasDeConjunto = () => (
  <Route element={<ZonaProtegida />}>
    <Route index element={<InicioDelConjunto />} />
    <Route path="resumen" element={<ResumenConjuntoPage />} />
    <Route path="porteria" element={<PorteriaPage />} />
    <Route path="entradas" element={<EntradasPage />} />
    <Route path="residentes" element={<ResidentesPage />} />
    <Route path="celadores" element={<CeladoresPage />} />
    <Route path="reportes" element={<ReportesConjuntoPage />} />
    <Route path="mi-conjunto" element={<MiConjuntoPage />} />
  </Route>
);

export default function App() {
  const { ambito } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {ambito === AMBITO_NEGOCIO ? rutasDeNegocio() : rutasDeConjunto()}

        <Route
          path="*"
          element={
            <EmptyState
              icon="search"
              title="Esta página no existe"
              text="Revisa la dirección o vuelve al inicio."
            />
          }
        />
      </Route>
    </Routes>
  );
}
